import { z } from 'zod';

const urlLength = 2048;

const urlValidator = z
  .string()
  .trim()
  .min(1, 'URL is required')
  .max(urlLength, `URL cannot exceed ${urlLength} characters`)
  .url('Must be a valid URL format')
  .refine((url) => {
    // Block dangerous protocols
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
    const lowerUrl = url.toLowerCase();
    return !dangerousProtocols.some((protocol) =>
      lowerUrl.startsWith(protocol),
    );
  }, 'URL contains unsafe protocol');

export const contentLegacyGuideSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  contentId: z.number().int().positive('Content ID must be positive'),
  count: z.number().int().min(1, 'Count must be non-negative'),
});

export const contentLinkSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  url: z.string().url('Must be a valid URL'),
  coverImage: z
    .string()
    .url('Must be a valid URL')
    .nullable()
    .or(z.literal(''))
    .optional(),
});

export const contentTabSchema = z.object({
  name: z.string().min(1, 'Name is required'),
});

export const extrasFormSchemaMap = {
  ContentLegacyGuide: contentLegacyGuideSchema,
  ContentLink: contentLinkSchema,
  ContentTab: contentTabSchema,
  ContentImage: z.object({
    title: z.string().min(1, 'Title is required').optional(),
    url: urlValidator.optional(),
    file: z.instanceof(File).optional(),
  }),
  ContentSlides: z.object({
    title: z.string().min(1, 'Title is required'),
    coverURL: z
      .string()
      .url('Must be a valid URL')
      .or(z.literal(''))
      .nullable()
      .optional(),
    downloadable: z.boolean(),
  }),
  ContentPDF: z.object({
    title: z.string().min(1, 'Title is required'),
    url: urlValidator,
    coverURL: z
      .string()
      .url('Must be a valid URL')
      .nullable()
      .or(z.literal(''))
      .optional(),
    downloadable: z.boolean(),
  }),
} as const;

export const validateImage = async (
  file: File,
  minWidth = 1005,
  minHeight = 450,
): Promise<{ isValid: boolean; error?: string; url?: string }> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(img.src);

      if (img.width < minWidth || img.height < minHeight) {
        resolve({
          isValid: false,
          error: `Image must be larger than ${minWidth}x${minHeight} pixels.`,
        });
        return;
      }

      resolve({ isValid: true });
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve({
        isValid: false,
        error: 'Failed to load image. Please try a different file.',
      });
    };
  });
};
